/**
 * Campaign Home Page — Shared Client Module
 * Exposes window.CampaignHome namespace via IIFE.
 * Loaded once with defer from the Hero section.
 */
(function () {
  'use strict';

  if (window.__CampaignHomeLoaded) return;
  window.__CampaignHomeLoaded = true;

  /* ===================================================================
   * Constants
   * =================================================================== */
  var EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  var KLAVIYO_REVISION = '2024-10-15';
  var REQUEST_TIMEOUT_MS = 10000;

  function markCampaignBody() {
    if (document.body) {
      document.body.classList.add('cmp-campaign-home');
    }
  }

  markCampaignBody();

  /* ===================================================================
   * Klaviyo Helpers
   * =================================================================== */

  /**
   * Build the JSON:API subscription body for Klaviyo.
   * Optionally attaches a custom_source label and a Prime Perks intent property
   * so all 3 forms can share one List but be segmented in flows.
   *
   * @param {string} email
   * @param {string} listId
   * @param {{ source?: string, intent?: string }} [meta]
   * @returns {object}
   */
  function buildSubscriptionBody(email, listId, meta) {
    var profileAttrs = { email: email };

    if (meta && (meta.source || meta.intent)) {
      profileAttrs.properties = {};
      if (meta.intent) profileAttrs.properties.prime_perks_intent = meta.intent;
      if (meta.source) profileAttrs.properties.signup_source = meta.source;
    }

    var body = {
      data: {
        type: 'subscription',
        attributes: {
          profile: { data: { type: 'profile', attributes: profileAttrs } }
        },
        relationships: {
          list: { data: { type: 'list', id: listId } }
        }
      }
    };

    if (meta && meta.source) {
      body.data.attributes.custom_source = meta.source;
    }

    return body;
  }

  /**
   * Submit an email subscription to Klaviyo.
   * @param {{ email: string, companyId: string, listId: string, source?: string, intent?: string }} opts
   * @returns {Promise<void>}
   */
  async function subscribe(opts) {
    var email = opts.email;
    var companyId = opts.companyId;
    var listId = opts.listId;
    var source = opts.source;
    var intent = opts.intent;

    if (!EMAIL_REGEX.test(email)) throw new Error('invalid_email');
    if (!companyId || !listId) throw new Error('config_missing');

    var url = KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(companyId);
    var ctrl = new AbortController();
    var t = setTimeout(function () { ctrl.abort(); }, REQUEST_TIMEOUT_MS);

    try {
      var res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': KLAVIYO_REVISION
        },
        body: JSON.stringify(buildSubscriptionBody(email, listId, { source: source, intent: intent })),
        signal: ctrl.signal
      });
      if (!res.ok) throw new Error('http_error');
    } finally {
      clearTimeout(t);
    }
  }

  /* ===================================================================
   * FormController
   * Attaches to [data-campaign-form] elements.
   * =================================================================== */
  var FormController = {
    _instances: [],

    init: function () {
      var forms = document.querySelectorAll('[data-campaign-form]');
      for (var i = 0; i < forms.length; i++) {
        var el = forms[i];
        if (el._cmpFormInit) continue;
        el._cmpFormInit = true;
        this._attach(el);
      }
    },

    _attach: function (el) {
      var form = el.querySelector('form');
      if (!form) return;

      var instance = {
        el: el,
        form: form,
        formId: el.getAttribute('data-form-id') || '',
        companyId: el.getAttribute('data-klaviyo-company-id') || '',
        listId: el.getAttribute('data-klaviyo-list-id') || '',
        source: el.getAttribute('data-klaviyo-source') || '',
        intent: el.getAttribute('data-klaviyo-intent') || el.getAttribute('data-form-id') || '',
        successMessage: el.getAttribute('data-success-message') || '',
        errorMessage: el.getAttribute('data-error-message') || 'Something went wrong',
        state: 'idle',
        input: form.querySelector('input[type="email"]'),
        button: form.querySelector('button[type="submit"]'),
        buttonLabel: form.querySelector('button[type="submit"] [class*="label"]'),
        loadingLabel: form.querySelector('button[type="submit"]')
          ? form.querySelector('button[type="submit"]').getAttribute('data-loading-label')
          : '',
        status: form.querySelector('[role="status"]') || el.querySelector('[role="status"]'),
        spinner: form.querySelector('[class*="spinner"]')
      };
      instance.buttonDefaultLabel = instance.buttonLabel ? instance.buttonLabel.textContent : '';

      this._instances.push(instance);

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        FormController._handleSubmit(instance);
      });
    },

    _handleSubmit: async function (inst) {
      if (inst.state === 'submitting' || inst.state === 'success') return;

      var email = inst.input ? inst.input.value.trim() : '';

      // Validate email
      if (!EMAIL_REGEX.test(email)) {
        this._showError(inst, 'Please enter a valid email address.');
        return;
      }

      // Validate config
      if (!inst.companyId || !inst.listId) {
        this._showError(inst, inst.errorMessage);
        return;
      }

      // Set submitting state
      inst.state = 'submitting';
      if (inst.button) inst.button.disabled = true;
      if (inst.buttonLabel && inst.loadingLabel) inst.buttonLabel.textContent = inst.loadingLabel;
      if (inst.spinner) inst.spinner.hidden = false;
      this._setStatus(inst, '');

      try {
        await subscribe({
          email: email,
          companyId: inst.companyId,
          listId: inst.listId,
          source: inst.source,
          intent: inst.intent
        });

        // For the wishlist form, also bind email to the Swym device
        // so this shopper's saved wishlist is recoverable across sessions
        // and devices once they log in.
        if (inst.formId === 'wishlist' && typeof SwymWishlist !== 'undefined' && SwymWishlist.bindEmail) {
          try { SwymWishlist.bindEmail(email); } catch (e) { /* non-fatal */ }
        }

        inst.state = 'success';
        this._showSuccess(inst);
      } catch (err) {
        inst.state = 'error';
        this._showError(inst, inst.errorMessage);
        if (inst.button) inst.button.disabled = false;
        if (inst.buttonLabel && inst.buttonDefaultLabel) inst.buttonLabel.textContent = inst.buttonDefaultLabel;
        if (inst.spinner) inst.spinner.hidden = true;
      }
    },

    _showSuccess: function (inst) {
      if (inst.form) {
        inst.form.innerHTML = '<p class="cmp-form__success">' + this._escapeHtml(inst.successMessage) + '</p>';
      }
    },

    _showError: function (inst, msg) {
      inst.state = 'error';
      this._setStatus(inst, msg);
    },

    _setStatus: function (inst, msg) {
      if (inst.status) inst.status.textContent = msg;
    },

    _escapeHtml: function (str) {
      var div = document.createElement('div');
      div.appendChild(document.createTextNode(str));
      return div.innerHTML;
    }
  };


  /* ===================================================================
   * ModalController
   * Manages Activity Modal open/close/focus-trap.
   * Opens within 250 ms via opacity/transform CSS transition.
   * Closes on Escape, backdrop click, close button, or "Check It Out!".
   * Focus-traps Tab/Shift+Tab cycling inside the modal.
   * Restores focus to the originating Activity_Card on close.
   * =================================================================== */
  var ModalController = {
    _modal: null,
    _originator: null,
    _focusableEls: [],
    _open: false,

    init: function () {
      var modal = document.querySelector('[data-campaign-modal]');
      if (!modal || modal._cmpModalInit) return;
      modal._cmpModalInit = true;
      this._modal = modal;

      // Attach triggers
      var triggers = document.querySelectorAll('[data-cmp-activity-trigger]');
      for (var i = 0; i < triggers.length; i++) {
        triggers[i].addEventListener('click', this._onTriggerClick.bind(this));
        triggers[i].addEventListener('keydown', this._onTriggerKeydown.bind(this));
      }

      // Close button
      var closeBtn = modal.querySelector('[data-cmp-modal-close]');
      if (closeBtn) closeBtn.addEventListener('click', this.close.bind(this));

      // Backdrop click (click on the modal overlay outside content)
      modal.addEventListener('click', function (e) {
        if (e.target === modal) ModalController.close();
      });

      // Escape key
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && ModalController._open) {
          ModalController.close();
        }
      });

      // Action button ("Check It Out!")
      var actionBtn = modal.querySelector('[data-cmp-modal-action]');
      if (actionBtn) {
        actionBtn.addEventListener('click', function () {
          var actionUrl = modal.getAttribute('data-current-action-url') || '';
          var target = modal.getAttribute('data-current-target') || '';
          ModalController.close();
          if (actionUrl) {
            window.location.href = actionUrl;
          } else if (target) {
            SmoothScroll.scrollToId(target);
          }
        });
      }

      // Focus trap
      modal.addEventListener('keydown', this._trapFocus.bind(this));
    },

    open: function (triggerEl, data) {
      if (!this._modal) return;
      this._originator = triggerEl;
      this._open = true;

      // Populate modal content from data attributes
      var fields = ['title', 'timeline', 'description', 'point_1', 'point_2', 'point_3', 'action_label', 'target'];
      for (var i = 0; i < fields.length; i++) {
        var slot = this._modal.querySelector('[data-modal-slot="' + fields[i] + '"]');
        if (slot && data[fields[i]] !== undefined) {
          slot.textContent = data[fields[i]];
        }
      }
      this._modal.setAttribute('data-current-target', data.target || '');
      this._modal.setAttribute('data-current-action-url', data.action_url || '');

      var actionBtn = this._modal.querySelector('[data-cmp-modal-action]');
      if (actionBtn) {
        actionBtn.hidden = !data.action_label;
      }

      // Populate modal icon from template
      var iconSlot = this._modal.querySelector('[data-modal-slot="icon"]');
      if (iconSlot) {
        iconSlot.innerHTML = '';
        if (data.icon_name) {
          var tmpl = document.getElementById('cmp-icon-tmpl-' + data.icon_name);
          if (tmpl) {
            iconSlot.appendChild(tmpl.content.cloneNode(true));
          }
          iconSlot.hidden = false;
        } else {
          iconSlot.hidden = true;
        }
      }

      // Make modal visible in the DOM (display), then trigger transition
      this._modal.hidden = false;
      this._modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Force reflow so the browser registers the initial state before adding .is-open
      void this._modal.offsetHeight;
      this._modal.classList.add('is-open');

      // Gather focusable elements
      this._focusableEls = Array.from(
        this._modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      );
      if (this._focusableEls.length) this._focusableEls[0].focus();
    },

    close: function () {
      if (!this._modal || !this._open) return;
      this._open = false;

      // Remove the visible class to trigger the close transition
      this._modal.classList.remove('is-open');
      this._modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      // After the transition completes, hide the element from the DOM
      var modal = this._modal;
      var originator = this._originator;
      this._originator = null;
      var cleaned = false;

      function cleanup() {
        if (cleaned) return;
        cleaned = true;
        modal.removeEventListener('transitionend', onTransitionEnd);
        modal.hidden = true;
        if (originator) originator.focus();
      }

      var onTransitionEnd = function (e) {
        // Only respond to the modal's own opacity transition
        if (e.target === modal) cleanup();
      };

      // Listen for transitionend; fallback timeout in case transition doesn't fire
      modal.addEventListener('transitionend', onTransitionEnd);
      setTimeout(cleanup, 300);
    },

    _onTriggerClick: function (e) {
      var trigger = e.currentTarget;
      this._openFromTrigger(trigger);
    },

    _onTriggerKeydown: function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this._openFromTrigger(e.currentTarget);
      }
    },

    _openFromTrigger: function (trigger) {
      var data = {
        title: trigger.getAttribute('data-activity-title') || '',
        timeline: trigger.getAttribute('data-activity-timeline') || '',
        description: trigger.getAttribute('data-activity-description') || '',
        point_1: trigger.getAttribute('data-activity-point-1') || '',
        point_2: trigger.getAttribute('data-activity-point-2') || '',
        point_3: trigger.getAttribute('data-activity-point-3') || '',
        action_label: trigger.getAttribute('data-activity-action-label') || '',
        target: trigger.getAttribute('data-activity-target') || '',
        action_url: trigger.getAttribute('data-activity-action-url') || '',
        icon_name: trigger.getAttribute('data-activity-icon-name') || ''
      };
      this.open(trigger, data);
    },

    _trapFocus: function (e) {
      if (e.key !== 'Tab' || !this._open) return;
      var focusable = this._focusableEls;
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  };


  /* ===================================================================
   * TabController
   * Manages [role="tablist"] groups.
   * =================================================================== */
  var TabController = {
    _tablists: [],

    init: function () {
      var tablists = document.querySelectorAll('[role="tablist"]');
      for (var i = 0; i < tablists.length; i++) {
        var tl = tablists[i];
        if (tl._cmpTabInit) continue;
        tl._cmpTabInit = true;
        this._attach(tl);
      }
    },

    _attach: function (tablist) {
      var tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
      var instance = { tablist: tablist, tabs: tabs, activeIndex: 0 };
      this._tablists.push(instance);

      // Activate first tab by default (without stealing focus / scrolling on load)
      if (tabs.length) this._activate(instance, 0, false);

      for (var i = 0; i < tabs.length; i++) {
        (function (idx) {
          tabs[idx].addEventListener('click', function () {
            TabController._activate(instance, idx, true);
          });
          tabs[idx].addEventListener('keydown', function (e) {
            TabController._onKeydown(e, instance, idx);
          });
        })(i);
      }
    },

    _activate: function (instance, idx, focusTab) {
      var tabs = instance.tabs;
      for (var i = 0; i < tabs.length; i++) {
        var isActive = i === idx;
        tabs[i].setAttribute('aria-selected', isActive ? 'true' : 'false');
        tabs[i].setAttribute('tabindex', isActive ? '0' : '-1');
        var panelId = tabs[i].getAttribute('aria-controls');
        if (panelId) {
          var panel = document.getElementById(panelId);
          if (panel) panel.hidden = !isActive;
        }
      }
      instance.activeIndex = idx;
      // Only move focus in response to user interaction. Focusing during the
      // initial load would scroll the page to the (last-initialized) tablist.
      if (focusTab) tabs[idx].focus();
    },

    _onKeydown: function (e, instance, currentIdx) {
      var count = instance.tabs.length;
      var newIdx = currentIdx;

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        newIdx = (currentIdx + 1) % count;
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        newIdx = (currentIdx - 1 + count) % count;
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this._activate(instance, currentIdx, true);
        return;
      } else {
        return;
      }
      this._activate(instance, newIdx, true);
    }
  };

  /* ===================================================================
   * CarouselController
   * Manages carousel prev/next/dot/swipe.
   * =================================================================== */
  var CarouselController = {
    _instances: [],

    init: function () {
      var carousels = document.querySelectorAll('[data-cmp-carousel]');
      for (var i = 0; i < carousels.length; i++) {
        var el = carousels[i];
        if (el._cmpCarouselInit) continue;
        el._cmpCarouselInit = true;
        this._attach(el);
      }
    },

    _attach: function (el) {
      var track = el.querySelector('[data-cmp-carousel-track]');
      var items = track ? Array.from(track.children) : [];
      var dots = Array.from(el.querySelectorAll('[data-cmp-carousel-dot]'));
      var prevBtn = el.querySelector('[data-cmp-carousel-prev]');
      var nextBtn = el.querySelector('[data-cmp-carousel-next]');

      var instance = {
        el: el,
        track: track,
        items: items,
        dots: dots,
        currentIndex: 0,
        count: items.length,
        touchStartX: 0
      };
      this._instances.push(instance);

      if (prevBtn) prevBtn.addEventListener('click', function () { CarouselController._prev(instance); });
      if (nextBtn) nextBtn.addEventListener('click', function () { CarouselController._next(instance); });

      for (var i = 0; i < dots.length; i++) {
        (function (idx) {
          dots[idx].addEventListener('click', function () {
            CarouselController._goTo(instance, idx);
          });
        })(i);
      }

      // Touch/swipe support
      if (track) {
        track.addEventListener('touchstart', function (e) {
          instance.touchStartX = e.touches[0].clientX;
        }, { passive: true });

        track.addEventListener('touchend', function (e) {
          var delta = e.changedTouches[0].clientX - instance.touchStartX;
          if (Math.abs(delta) > 30) {
            if (delta < 0) CarouselController._next(instance);
            else CarouselController._prev(instance);
          }
        }, { passive: true });
      }
    },

    _next: function (inst) {
      if (inst.currentIndex < inst.count - 1) {
        this._goTo(inst, inst.currentIndex + 1);
      }
    },

    _prev: function (inst) {
      if (inst.currentIndex > 0) {
        this._goTo(inst, inst.currentIndex - 1);
      }
    },

    _goTo: function (inst, idx) {
      if (idx < 0 || idx >= inst.count) return;
      inst.currentIndex = idx;
      // Translate the track with 300ms transition
      if (inst.track) {
        inst.track.style.transition = 'transform 300ms ease';
        inst.track.style.transform = 'translateX(-' + (idx * 100) + '%)';
      }
      // Update dots
      for (var i = 0; i < inst.dots.length; i++) {
        inst.dots[i].classList.toggle('is-active', i === idx);
        inst.dots[i].setAttribute('aria-selected', i === idx ? 'true' : 'false');
      }
    }
  };


  /* ===================================================================
   * AccordionController
   * Manages FAQ accordion with single-open semantics.
   * =================================================================== */
  var AccordionController = {
    _instances: [],

    init: function () {
      var accordions = document.querySelectorAll('[data-cmp-accordion]');
      for (var i = 0; i < accordions.length; i++) {
        var el = accordions[i];
        if (el._cmpAccordionInit) continue;
        el._cmpAccordionInit = true;
        this._attach(el);
      }
    },

    _attach: function (el) {
      var toggles = Array.from(el.querySelectorAll('[aria-expanded]'));
      var instance = { el: el, toggles: toggles, openIndex: -1 };
      this._instances.push(instance);

      for (var i = 0; i < toggles.length; i++) {
        (function (idx) {
          toggles[idx].addEventListener('click', function () {
            AccordionController._toggle(instance, idx);
          });
          toggles[idx].addEventListener('keydown', function (e) {
            if (e.key === ' ') {
              e.preventDefault();
              AccordionController._toggle(instance, idx);
            } else if (e.key === 'Enter') {
              AccordionController._toggle(instance, idx);
            }
          });
        })(i);
      }
    },

    _toggle: function (instance, idx) {
      var toggles = instance.toggles;
      var isCurrentlyOpen = toggles[idx].getAttribute('aria-expanded') === 'true';

      // Close all panels first (single-open invariant)
      for (var i = 0; i < toggles.length; i++) {
        toggles[i].setAttribute('aria-expanded', 'false');
        var panelId = toggles[i].getAttribute('aria-controls');
        if (panelId) {
          var panel = document.getElementById(panelId);
          if (panel) panel.hidden = true;
        }
      }

      // If the clicked toggle was closed, open it
      if (!isCurrentlyOpen) {
        toggles[idx].setAttribute('aria-expanded', 'true');
        var targetId = toggles[idx].getAttribute('aria-controls');
        if (targetId) {
          var targetPanel = document.getElementById(targetId);
          if (targetPanel) targetPanel.hidden = false;
        }
        instance.openIndex = idx;
      } else {
        instance.openIndex = -1;
      }
    }
  };

  /* ===================================================================
   * SmoothScroll
   * Provides scrollToId(id) method with precise timing control.
   * - Animates over 300–600 ms when reduced motion is off (easeInOutCubic).
   * - Snaps within 50 ms when reduced motion is on.
   * - Aligns the target element's top edge within 2 px of the viewport top
   *   (or below any sticky header if present).
   * - Returns immediately (no scroll mutation) if the target id is missing.
   * =================================================================== */
  var SmoothScroll = {
    /** Duration in ms for animated scroll (reduced motion off) */
    DURATION: 450,
    /** Duration in ms for instant snap (reduced motion on) */
    SNAP_DURATION: 16,

    /**
     * Scroll to the element with the given id.
     * @param {string} id - The target element id (without #).
     */
    scrollToId: function (id) {
      if (!id) return;
      var target = document.getElementById(id);
      if (!target) return;

      var reducedMotion = document.documentElement.classList.contains('cmp-reduce-motion');
      var targetTop = this._getTargetOffset(target);
      var startY = window.pageYOffset || document.documentElement.scrollTop;
      var distance = targetTop - startY;

      // If already at the target position (within 2px), do nothing
      if (Math.abs(distance) <= 2) return;

      if (reducedMotion) {
        // Snap immediately (within 50 ms budget — one rAF frame)
        window.scrollTo(0, targetTop);
        return;
      }

      // Animated scroll using requestAnimationFrame
      var duration = this.DURATION;
      var startTime = null;
      var self = this;

      function step(timestamp) {
        if (!startTime) startTime = timestamp;
        var elapsed = timestamp - startTime;
        var progress = Math.min(elapsed / duration, 1);
        var eased = self._easeInOutCubic(progress);

        window.scrollTo(0, startY + distance * eased);

        if (progress < 1) {
          requestAnimationFrame(step);
        }
      }

      requestAnimationFrame(step);
    },

    /**
     * Calculate the target scroll offset (top edge of element relative to document).
     * Accounts for any sticky header if present.
     * @param {HTMLElement} el
     * @returns {number}
     */
    _getTargetOffset: function (el) {
      var rect = el.getBoundingClientRect();
      var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      return rect.top + scrollTop;
    },

    /**
     * Easing function: easeInOutCubic
     * @param {number} t - Progress 0..1
     * @returns {number}
     */
    _easeInOutCubic: function (t) {
      return t < 0.5
        ? 4 * t * t * t
        : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }
  };

  /* ===================================================================
   * ObserverEntrance
   * Applies IntersectionObserver-based entrance animations.
   * =================================================================== */
  var ObserverEntrance = {
    _observer: null,

    init: function () {
      this.applyEntrances(document);
    },

    applyEntrances: function (root) {
      var targets = root.querySelectorAll('[data-cmp-entrance]');
      if (!targets.length) return;

      var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // If IntersectionObserver is unavailable or reduced motion is active,
      // set all elements visible immediately
      if (reducedMotion || !('IntersectionObserver' in window)) {
        for (var i = 0; i < targets.length; i++) {
          targets[i].classList.add('is-visible');
        }
        return;
      }

      var observer = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (entry.isIntersecting && entry.intersectionRatio >= 0.1) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        }
      }, { threshold: 0.1 });

      for (var j = 0; j < targets.length; j++) {
        targets[j].classList.add('cmp-entrance');
        observer.observe(targets[j]);
      }

      this._observer = observer;
    }
  };


  /* ===================================================================
   * ReducedMotionWatcher
   * Listens for prefers-reduced-motion changes and toggles
   * documentElement.classList 'cmp-reduce-motion'.
   * =================================================================== */
  var ReducedMotionWatcher = {
    _mql: null,

    init: function () {
      this._mql = window.matchMedia('(prefers-reduced-motion: reduce)');

      // Set initial state
      document.documentElement.classList.toggle('cmp-reduce-motion', this._mql.matches);
      if (document.body) document.body.classList.toggle('cmp-reduce-motion', this._mql.matches);

      // Listen for changes
      this._mql.addEventListener('change', this._onChange.bind(this));
    },

    _onChange: function (e) {
      var reduced = e.matches;
      document.documentElement.classList.toggle('cmp-reduce-motion', reduced);
      if (document.body) document.body.classList.toggle('cmp-reduce-motion', reduced);

      // Notify MarqueeController
      MarqueeController.onReducedMotionChange(reduced);

      // Notify ObserverEntrance — if reduced motion is now active,
      // make all entrance elements visible immediately
      if (reduced) {
        var targets = document.querySelectorAll('[data-cmp-entrance]:not(.is-visible)');
        for (var i = 0; i < targets.length; i++) {
          targets[i].classList.add('is-visible');
        }
      }
    }
  };

  /* ===================================================================
   * SwymWishlist
   * Real Wishlist Plus (Swym) integration replacing the in-memory
   * HeartToggleStore. Wraps swat.* calls behind a queue that defers
   * actions until the Swym SDK has loaded (via window.SwymCallbacks).
   *
   * Behavior:
   *   • On init, looks for a section with [data-swym-enabled="true"]
   *     and reads its data-swym-list-name + data-swym-activity-tag.
   *   • Resolves (creates if missing) a list with that name; caches lid.
   *   • Fetches list contents and pre-fills hearts that are already saved.
   *   • Heart click → swat.addToList / deleteFromList using
   *     data-swym-empi/epi/du from the card. Falls back to in-session
   *     toggle if the card has no linked product.
   *   • Add-all click → batch add for every pick with linked product.
   *   • If swat is unavailable (Swym not installed / API not enabled),
   *     gracefully degrades to the original in-memory behavior so the
   *     UI never breaks.
   * =================================================================== */
  var SwymWishlist = {
    _enabled: false,
    _swat: null,
    _ready: false,
    _readyResolvers: [],
    _lid: null,
    _listName: '',
    _activityTag: '',
    _filledKeys: new Set(),     // keys of items currently in the list
    _localState: new Map(),     // composite-key fallback for un-linked cards
    _hearts: [],

    init: function () {
      var section = document.querySelector('[data-swym-enabled="true"]');
      this._enabled = !!section;
      this._listName = section ? (section.getAttribute('data-swym-list-name') || 'Prime Perks 2026 Wishlist') : '';
      this._activityTag = section ? (section.getAttribute('data-swym-activity-tag') || 'prime_perks_2026') : '';

      // Always wire DOM listeners — falls back to local state when Swym is off
      var hearts = document.querySelectorAll('[data-cmp-heart]');
      this._hearts = Array.prototype.slice.call(hearts);
      for (var i = 0; i < hearts.length; i++) {
        var btn = hearts[i];
        if (btn._cmpHeartInit) continue;
        btn._cmpHeartInit = true;
        btn.addEventListener('click', this._onHeartClick.bind(this));
      }

      var addAllBtns = document.querySelectorAll('[data-cmp-add-all]');
      for (var j = 0; j < addAllBtns.length; j++) {
        var addBtn = addAllBtns[j];
        if (addBtn._cmpAddAllInit) continue;
        addBtn._cmpAddAllInit = true;
        addBtn.addEventListener('click', this._onAddAll.bind(this));
      }

      if (this._enabled) {
        this._bootstrapSwym();
      }
    },

    /**
     * Defer until Swym SDK is loaded, then resolve list + initial state.
     */
    _bootstrapSwym: function () {
      var self = this;
      function onLoad(swat) {
        self._swat = swat;
        self._resolveList(function () {
          self._fetchInitialState(function () {
            self._ready = true;
            for (var k = 0; k < self._readyResolvers.length; k++) {
              try { self._readyResolvers[k](); } catch (e) { }
            }
            self._readyResolvers = [];
          });
        });

        // Bind Swym's own UI events so hearts stay in sync if user
        // adds/removes from elsewhere (PDP, default UI, etc.)
        try {
          if (swat.evtLayer && typeof swat.evtLayer.addEventListener === 'function') {
            swat.evtLayer.addEventListener('sw:addedtowishlist', function (e) {
              var d = e && e.detail && e.detail.d;
              if (d && d.empi) self._markFilledByEmpi(d.empi, true);
            });
            swat.evtLayer.addEventListener('sw:removedfromwishlist', function (e) {
              var d = e && e.detail && e.detail.d;
              if (d && d.empi) self._markFilledByEmpi(d.empi, false);
            });
          }
        } catch (e) { /* non-fatal */ }
      }

      if (!window.SwymCallbacks) window.SwymCallbacks = [];
      window.SwymCallbacks.push(onLoad);
    },

    /**
     * Find or create the activity-specific list (createList is idempotent
     * by name — returns existing list if name matches).
     */
    _resolveList: function (done) {
      var self = this;
      if (!this._swat || typeof this._swat.createList !== 'function') {
        return done && done();
      }

      this._swat.createList(
        { lname: this._listName },
        function (list) {
          if (list && list.lid) self._lid = list.lid;
          done && done();
        },
        function (err) {
          // createList can fail on quota / permission issues. Try fetchLists
          // to see if the list already exists.
          if (typeof self._swat.fetchLists === 'function') {
            self._swat.fetchLists(
              {},
              function (lists) {
                if (Array.isArray(lists)) {
                  for (var i = 0; i < lists.length; i++) {
                    if (lists[i].lname === self._listName) {
                      self._lid = lists[i].lid;
                      break;
                    }
                  }
                }
                done && done();
              },
              function () { done && done(); }
            );
          } else {
            done && done();
          }
        }
      );
    },

    _fetchInitialState: function (done) {
      var self = this;
      if (!this._swat || !this._lid || typeof this._swat.fetchListCtx !== 'function') {
        return done && done();
      }

      this._swat.fetchListCtx(
        { lid: this._lid },
        function (items) {
          if (Array.isArray(items)) {
            for (var i = 0; i < items.length; i++) {
              var it = items[i];
              if (it && it.empi) self._filledKeys.add('empi:' + it.empi);
            }
          }
          self._syncAllHeartUI();
          done && done();
        },
        function () { done && done(); }
      );
    },

    _syncAllHeartUI: function () {
      for (var i = 0; i < this._hearts.length; i++) {
        var btn = this._hearts[i];
        var product = this._readProductFromBtn(btn);
        if (product && product.empi) {
          this._updateHeartUI(btn, this._filledKeys.has('empi:' + product.empi));
        }
      }
    },

    _markFilledByEmpi: function (empi, filled) {
      var key = 'empi:' + empi;
      if (filled) this._filledKeys.add(key); else this._filledKeys.delete(key);
      for (var i = 0; i < this._hearts.length; i++) {
        var btn = this._hearts[i];
        var product = this._readProductFromBtn(btn);
        if (product && String(product.empi) === String(empi)) {
          this._updateHeartUI(btn, filled);
          if (filled) this._showTransientIndicator(btn);
        }
      }
    },

    _readProductFromBtn: function (btn) {
      var card = btn.closest('[data-swym-empi]');
      if (!card) return null;
      var empi = card.getAttribute('data-swym-empi');
      var epi = card.getAttribute('data-swym-epi');
      var du = card.getAttribute('data-swym-du');
      if (!empi || !epi || !du) return null;
      return {
        empi: parseInt(empi, 10),
        epi: parseInt(epi, 10),
        du: du
      };
    },

    _onHeartClick: function (e) {
      var btn = e.currentTarget;
      var product = this._readProductFromBtn(btn);

      // Fallback: card has no linked product (e.g. content-only) — local toggle
      if (!product || !this._enabled) {
        return this._localToggle(btn);
      }

      var key = 'empi:' + product.empi;
      var willFill = !this._filledKeys.has(key);

      // Optimistic UI
      this._updateHeartUI(btn, willFill);
      if (willFill) this._showTransientIndicator(btn);

      var self = this;
      this._whenReady(function () {
        if (!self._swat || !self._lid) {
          // Revert if Swym never loaded
          self._updateHeartUI(btn, !willFill);
          return;
        }
        if (willFill) {
          self._addToList(product, function () {
            self._filledKeys.add(key);
          }, function () {
            self._updateHeartUI(btn, false);
          });
        } else {
          self._deleteFromList(product, function () {
            self._filledKeys.delete(key);
          }, function () {
            self._updateHeartUI(btn, true);
          });
        }
      });
    },

    _localToggle: function (btn) {
      var itemId = btn.getAttribute('data-item-id') || '';
      var sectionId = btn.closest('[id]') ? btn.closest('[id]').id : '';
      var key = sectionId + '::' + itemId;
      var current = this._localState.get(key) || false;
      var next = !current;
      this._localState.set(key, next);
      this._updateHeartUI(btn, next);
      if (next) this._showTransientIndicator(btn);
    },

    _onAddAll: function (e) {
      var container = e.currentTarget.closest('[data-cmp-creator-card]') || e.currentTarget.closest('.cmp-creator-card');
      if (!container) return;

      var hearts = container.querySelectorAll('[data-cmp-heart]');
      var self = this;

      this._whenReady(function () {
        for (var i = 0; i < hearts.length; i++) {
          var btn = hearts[i];
          var product = self._readProductFromBtn(btn);
          if (!product || !self._enabled || !self._swat || !self._lid) {
            // local-only fallback
            if (!self._localState.get((btn.closest('[id]') ? btn.closest('[id]').id : '') + '::' + (btn.getAttribute('data-item-id') || ''))) {
              self._localToggle(btn);
            }
            continue;
          }
          var key = 'empi:' + product.empi;
          if (self._filledKeys.has(key)) continue;

          // Optimistic UI per item
          self._updateHeartUI(btn, true);
          self._showTransientIndicator(btn);

          (function (b, p, k) {
            self._addToList(p, function () { self._filledKeys.add(k); }, function () { self._updateHeartUI(b, false); });
          })(btn, product, key);
        }
      });
    },

    _addToList: function (product, onSuccess, onError) {
      try {
        var payload = {
          epi: product.epi,
          empi: product.empi,
          du: product.du,
          source: 'featured-grid',
          cprops: this._activityTag ? { activity: this._activityTag } : undefined
        };
        this._swat.addToList(this._lid, payload, function () {
          if (onSuccess) onSuccess();
        }, function () {
          if (onError) onError();
        });
      } catch (e) {
        if (onError) onError();
      }
    },

    _deleteFromList: function (product, onSuccess, onError) {
      try {
        if (typeof this._swat.deleteFromList !== 'function') {
          // Older SDK fallback name
          if (typeof this._swat.removeFromList === 'function') {
            this._swat.removeFromList(this._lid, product, onSuccess, onError);
            return;
          }
        }
        this._swat.deleteFromList(this._lid, {
          epi: product.epi,
          empi: product.empi,
          du: product.du
        }, function () {
          if (onSuccess) onSuccess();
        }, function () {
          if (onError) onError();
        });
      } catch (e) {
        if (onError) onError();
      }
    },

    /**
     * Bind email submitted via the Wishlist form to the current Swym
     * device session. Lets logged-out shoppers' wishlist follow them
     * once they sign in or re-visit on another device.
     */
    bindEmail: function (email) {
      if (!email || !this._enabled) return;
      var self = this;
      this._whenReady(function () {
        if (!self._swat) return;
        try {
          if (typeof self._swat.saveShopperContext === 'function') {
            self._swat.saveShopperContext({ email: email }, function () { }, function () { });
          } else if (typeof self._swat.subscribeForBackInStockAlert === 'function') {
            // No-op: only for back-in-stock; included to silence linters
          }
        } catch (e) { /* non-fatal */ }
      });
    },

    _whenReady: function (cb) {
      if (this._ready || !this._enabled) {
        try { cb(); } catch (e) { }
      } else {
        this._readyResolvers.push(cb);
      }
    },

    _updateHeartUI: function (btn, filled) {
      btn.classList.toggle('is-filled', filled);
      btn.setAttribute('aria-pressed', filled ? 'true' : 'false');
      var emptyIcon = btn.querySelector('[class*="heart-empty"]');
      var filledIcon = btn.querySelector('[class*="heart-filled"]');
      if (emptyIcon) emptyIcon.hidden = filled;
      if (filledIcon) filledIcon.hidden = !filled;
    },

    _showTransientIndicator: function (btn) {
      var card = btn.closest('[data-cmp-card]') || btn.closest('[data-cmp-entrance]') || btn.parentElement;
      if (!card) return;
      var indicator = card.querySelector('[data-cmp-heart-indicator]');
      if (!indicator) return;
      indicator.hidden = false;
      indicator.classList.add('is-active');
      setTimeout(function () {
        indicator.hidden = true;
        indicator.classList.remove('is-active');
      }, 5000);
    }
  };

  // Backwards-compatible alias for existing namespace consumers
  var HeartToggleStore = SwymWishlist;


  /* ===================================================================
   * MarqueeController
   * requestAnimationFrame-based marquee animation.
   * Scans for [data-cmp-marquee] elements and reads data-cmp-marquee-speed
   * and data-cmp-marquee-gap attributes.
   * Sets will-change: transform on start, removes it on pause/reduced-motion,
   * and switches to a static rendered state when cmp-reduce-motion is active.
   * =================================================================== */
  var MarqueeController = {
    _instances: [],
    _paused: false,

    init: function () {
      var marquees = document.querySelectorAll('[data-cmp-marquee]');
      for (var i = 0; i < marquees.length; i++) {
        var el = marquees[i];
        if (el._cmpMarqueeInit) continue;
        el._cmpMarqueeInit = true;
        this._attach(el);
      }
    },

    _attach: function (el) {
      var track = el.querySelector('[data-cmp-marquee-track]');
      if (!track) return;

      var speed = parseFloat(el.getAttribute('data-cmp-marquee-speed')) || 60; // px/s
      var gap = parseInt(el.getAttribute('data-cmp-marquee-gap'), 10) || 8; // px

      var instance = {
        el: el,
        track: track,
        speed: speed,
        gap: gap,
        offset: 0,
        width: 0,
        rafId: null,
        lastTime: 0
      };
      this._instances.push(instance);

      // Check reduced motion before starting
      var reducedMotion = document.documentElement.classList.contains('cmp-reduce-motion');
      if (reducedMotion) {
        el.classList.add('cmp-marquee--static');
        return;
      }

      this._start(instance);
    },

    _start: function (inst) {
      inst.track.style.willChange = 'transform';
      inst.width = inst.track.scrollWidth / 2; // Two copies of the text
      inst.lastTime = performance.now();

      var self = this;
      function animate(now) {
        if (self._paused) {
          inst.rafId = null;
          return;
        }
        var delta = (now - inst.lastTime) / 1000;
        inst.lastTime = now;
        inst.offset += inst.speed * delta;

        if (inst.offset >= inst.width) {
          inst.offset -= inst.width;
        }

        inst.track.style.transform = 'translateX(-' + inst.offset + 'px)';
        inst.rafId = requestAnimationFrame(animate);
      }

      inst.rafId = requestAnimationFrame(animate);
    },

    _stop: function (inst) {
      if (inst.rafId) {
        cancelAnimationFrame(inst.rafId);
        inst.rafId = null;
      }
      inst.track.style.willChange = '';
      inst.track.style.transform = '';
    },

    onReducedMotionChange: function (reduced) {
      this._paused = reduced;
      for (var i = 0; i < this._instances.length; i++) {
        var inst = this._instances[i];
        if (reduced) {
          this._stop(inst);
          inst.el.classList.add('cmp-marquee--static');
        } else {
          inst.el.classList.remove('cmp-marquee--static');
          this._start(inst);
        }
      }
    }
  };

  /* ===================================================================
   * Mobile breakpoint watcher
   * Toggles body.cmp-mobile for View More/View Less logic.
   * On mobile: hides Product_Cards beyond index 3 (4th card onwards)
   * in each tab panel by adding cmp-wl__hidden class.
   * Renders a "View More" toggle button per panel. On click: reveals
   * hidden cards, changes label to "View Less". On "View Less" click:
   * hides cards again, changes label to "View More".
   * Labels are read from data-view-more-label and data-view-less-label
   * attributes on the section or panel.
   * When switching from mobile to desktop: removes all cmp-wl__hidden
   * classes and hides the toggle buttons.
   * =================================================================== */
  var MobileWatcher = {
    _mql: null,
    _toggleButtons: [],

    init: function () {
      this._mql = window.matchMedia('(max-width: 767px)');
      var isMobile = this._mql.matches;
      document.body.classList.toggle('cmp-mobile', isMobile);
      this._applyViewMore(isMobile);
      this._mql.addEventListener('change', this._onChange.bind(this));
    },

    _onChange: function (e) {
      var isMobile = e.matches;
      document.body.classList.toggle('cmp-mobile', isMobile);
      this._applyViewMore(isMobile);
    },

    /**
     * Apply or remove View More/View Less logic on all tab panels.
     * @param {boolean} isMobile
     */
    _applyViewMore: function (isMobile) {
      var panels = document.querySelectorAll('[role="tabpanel"]');
      for (var i = 0; i < panels.length; i++) {
        var panel = panels[i];
        this._processPanel(panel, isMobile);
      }
    },

    /**
     * Process a single tab panel: hide/show cards and manage toggle button.
     * @param {HTMLElement} panel
     * @param {boolean} isMobile
     */
    _processPanel: function (panel, isMobile) {
      // Determine the threshold: Accessory section uses 3 (index 2), Wishlist uses 4 (index 3)
      var section = panel.closest('[data-cmp-view-more-threshold]');
      var threshold = section
        ? parseInt(section.getAttribute('data-cmp-view-more-threshold'), 10) || 4
        : 4;

      var cards = Array.from(panel.querySelectorAll('[data-cmp-card]'));

      if (!isMobile) {
        // Desktop: remove all hidden classes and hide toggle buttons
        for (var i = 0; i < cards.length; i++) {
          cards[i].classList.remove('cmp-wl__hidden');
        }
        var existingToggle = panel.querySelector('[data-cmp-view-more-toggle]');
        if (existingToggle) {
          existingToggle.hidden = true;
          existingToggle.setAttribute('data-expanded', 'false');
          // Reset label to "View More"
          var viewMoreLabel = this._getViewMoreLabel(panel);
          existingToggle.textContent = viewMoreLabel;
        }
        return;
      }

      // Mobile: hide cards beyond the threshold
      var hasHiddenCards = cards.length > threshold;
      for (var j = 0; j < cards.length; j++) {
        if (j >= threshold) {
          cards[j].classList.add('cmp-wl__hidden');
        } else {
          cards[j].classList.remove('cmp-wl__hidden');
        }
      }

      // Manage toggle button
      var toggle = panel.querySelector('[data-cmp-view-more-toggle]');
      if (hasHiddenCards) {
        if (!toggle) {
          toggle = this._createToggleButton(panel);
        }
        toggle.hidden = false;
        toggle.setAttribute('data-expanded', 'false');
        toggle.textContent = this._getViewMoreLabel(panel);
      } else if (toggle) {
        toggle.hidden = true;
      }
    },

    /**
     * Create a View More toggle button and append it to the panel.
     * @param {HTMLElement} panel
     * @returns {HTMLElement}
     */
    _createToggleButton: function (panel) {
      var btn = document.createElement('button');
      btn.setAttribute('data-cmp-view-more-toggle', '');
      btn.setAttribute('data-expanded', 'false');
      btn.className = 'cmp-view-more-toggle';
      btn.type = 'button';
      btn.textContent = this._getViewMoreLabel(panel);
      panel.appendChild(btn);

      var self = this;
      btn.addEventListener('click', function () {
        self._onToggleClick(panel, btn);
      });

      self._toggleButtons.push(btn);
      return btn;
    },

    /**
     * Handle View More / View Less toggle click.
     * @param {HTMLElement} panel
     * @param {HTMLElement} btn
     */
    _onToggleClick: function (panel, btn) {
      var isExpanded = btn.getAttribute('data-expanded') === 'true';

      var section = panel.closest('[data-cmp-view-more-threshold]');
      var threshold = section
        ? parseInt(section.getAttribute('data-cmp-view-more-threshold'), 10) || 4
        : 4;

      var cards = Array.from(panel.querySelectorAll('[data-cmp-card]'));

      if (isExpanded) {
        // Collapse: hide cards beyond threshold
        for (var i = threshold; i < cards.length; i++) {
          cards[i].classList.add('cmp-wl__hidden');
        }
        btn.setAttribute('data-expanded', 'false');
        btn.textContent = this._getViewMoreLabel(panel);
      } else {
        // Expand: reveal all hidden cards
        for (var j = 0; j < cards.length; j++) {
          cards[j].classList.remove('cmp-wl__hidden');
        }
        btn.setAttribute('data-expanded', 'true');
        btn.textContent = this._getViewLessLabel(panel);
      }
    },

    /**
     * Get the "View More" label from data attributes or locale default.
     * Checks the panel first, then the closest section.
     * @param {HTMLElement} panel
     * @returns {string}
     */
    _getViewMoreLabel: function (panel) {
      var label = panel.getAttribute('data-view-more-label');
      if (label) return label;
      var section = panel.closest('[data-view-more-label]');
      if (section) return section.getAttribute('data-view-more-label');
      return 'View More';
    },

    /**
     * Get the "View Less" label from data attributes or locale default.
     * Checks the panel first, then the closest section.
     * @param {HTMLElement} panel
     * @returns {string}
     */
    _getViewLessLabel: function (panel) {
      var label = panel.getAttribute('data-view-less-label');
      if (label) return label;
      var section = panel.closest('[data-view-less-label]');
      if (section) return section.getAttribute('data-view-less-label');
      return 'View Less';
    }
  };

  /* ===================================================================
   * DOMContentLoaded Bootstrapping
   * Scans for data-campaign-* hooks and initializes each module
   * idempotently so the page works regardless of which sections
   * are present.
   * =================================================================== */
  function bootstrap() {
    markCampaignBody();

    // Initialize reduced motion watcher first (affects other modules)
    ReducedMotionWatcher.init();

    // Form controllers (data-campaign-form)
    if (document.querySelector('[data-campaign-form]')) {
      FormController.init();
    }

    // Modal controller (data-campaign-modal)
    if (document.querySelector('[data-campaign-modal]')) {
      ModalController.init();
    }

    // Marquee (data-cmp-marquee)
    if (document.querySelector('[data-cmp-marquee]')) {
      MarqueeController.init();
    }

    // Tab controllers ([role="tablist"])
    if (document.querySelector('[role="tablist"]')) {
      TabController.init();
    }

    // Mobile breakpoint watcher — must run after TabController so panels exist
    MobileWatcher.init();

    // Carousel (data-cmp-carousel)
    if (document.querySelector('[data-cmp-carousel]')) {
      CarouselController.init();
    }

    // Accordion (data-cmp-accordion)
    if (document.querySelector('[data-cmp-accordion]')) {
      AccordionController.init();
    }

    // Entrance animations (data-cmp-entrance)
    if (document.querySelector('[data-cmp-entrance]')) {
      ObserverEntrance.init();
    }

    // Heart toggles (data-cmp-heart)
    if (document.querySelector('[data-cmp-heart]')) {
      HeartToggleStore.init();
    }

    // Hero scroll-to-activities
    var heroScrollTriggers = document.querySelectorAll('[data-cmp-scroll-target]');
    for (var i = 0; i < heroScrollTriggers.length; i++) {
      (function (trigger) {
        var targetId = trigger.getAttribute('data-cmp-scroll-target');
        trigger.addEventListener('click', function (e) {
          if (
            e.target !== trigger &&
            e.target.closest &&
            e.target.closest('form, input, button, a, select, textarea, label')
          ) {
            return;
          }
          SmoothScroll.scrollToId(targetId);
        });
        trigger.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            SmoothScroll.scrollToId(targetId);
          }
        });
      })(heroScrollTriggers[i]);
    }
  }

  // Run bootstrap on DOMContentLoaded (or immediately if already loaded)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  /* ===================================================================
   * Expose public namespace
   * =================================================================== */
  window.CampaignHome = {
    _loaded: true,
    EMAIL_REGEX: EMAIL_REGEX,
    KLAVIYO_ENDPOINT: KLAVIYO_ENDPOINT,
    KLAVIYO_REVISION: KLAVIYO_REVISION,
    REQUEST_TIMEOUT_MS: REQUEST_TIMEOUT_MS,
    buildSubscriptionBody: buildSubscriptionBody,
    subscribe: subscribe,
    FormController: FormController,
    ModalController: ModalController,
    TabController: TabController,
    CarouselController: CarouselController,
    AccordionController: AccordionController,
    SmoothScroll: SmoothScroll,
    ObserverEntrance: ObserverEntrance,
    ReducedMotionWatcher: ReducedMotionWatcher,
    HeartToggleStore: HeartToggleStore,
    SwymWishlist: SwymWishlist,
    MarqueeController: MarqueeController,
    MobileWatcher: MobileWatcher
  };

})();
